import { Brand } from "@mixitone/components";
import clsx from "clsx";
import tauri from "lib/tauri";
import type { ReactNode } from "react";

const wwwUrl = import.meta.env.VITE_WWW;

interface SplitScreenLayoutProps {
  showBack?: boolean;
  onBack?: () => void;
  title?: ReactNode;
  children: ReactNode;
  rightChildren?: ReactNode;
}

const SplitScreenLayout: React.FC<SplitScreenLayoutProps> = ({
  children,
  rightChildren,
  onBack,
  title,
  showBack,
}) => {
  const reallyShowBack = showBack ?? true;

  const handleOnBack = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (onBack) {
      e.preventDefault();
      onBack();
    }
  };

  return (
    <div className={clsx("h-full overflow-hidden bg-brand-600", { "sm:p-10": !tauri.enabled })}>
      <div className="flex flex-col h-full shadow-2xl shadow-slate-700 sm:flex-row">
        <div
          className={clsx(
            "flex w-auto bg-slate-800 bg-gradient-to-r from-slate-700 py-4 to-slate-800 sm:w-1/3 sm:flex-col sm:bg-gradient-to-b lg:w-1/2",
            { "sm:rounded-l-lg": !tauri.enabled },
          )}
        >
          <div data-tauri-drag-region className="shrink-0 p-4" />
          <div data-tauri-drag-region className="flex flex-grow items-center justify-center">
            <div className="hidden sm:block">
              {rightChildren || <Brand className="text-4xl" color="dark" />}
            </div>
            <div className="sm:hidden">
              <Brand className="text-3xl" color="dark" />
            </div>
          </div>
          <div className="w-8 sm:hidden" />
        </div>
        <div className="flex min-h-0 flex-col flex-grow overflow-y-auto bg-white sm:w-2/3 lg:w-1/2 sm:rounded-r-lg">
          <a
            href={wwwUrl}
            onClick={handleOnBack}
            className={clsx(
              "m-4 inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors w-fit",
              {
                invisible: !reallyShowBack,
              },
            )}
            aria-label="Back"
          >
            ← Back
          </a>
          <div
            className={clsx(
              "flex h-full flex-grow flex-col justify-center bg-white sm:flex-grow-0 sm:items-center",
              { "sm:rounded-r-lg": !tauri.enabled },
            )}
          >
            <div className="w-full px-8 py-6 sm:px-16">
              {title && <h1 className="short:mb-6 mb-10 short:text-3xl text-4xl text-gray-400">{title}</h1>}
              {children}
            </div>
          </div>
          <div className="flex justify-center text-xs">
            <p className="text-gray-500">© Mixitone {new Date().getFullYear()}</p>
            <p className="mx-2 text-gray-500">|</p>
            <div>
              <a
                href={`${wwwUrl}/terms`}
                target="_blank"
                className="text-gray-500 hover:underline"
                rel="noopener noreferrer"
              >
                Terms of Service
              </a>
            </div>
            <p className="mx-2 text-gray-500">|</p>
            <div>
              <a
                href={`${wwwUrl}/privacy`}
                target="_blank"
                className="text-gray-500 hover:underline"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SplitScreenLayout;
